import React, { useState } from "react";
import "./sharedCatalogue.css";
import notifications from "../../../common/Components/Notifications/Notification";
import {
  CATALOGUE_SHARE_COUNTRIES,
  DEFAULT_SHARE_COUNTRY_ISO,
  isPlausibleSharePhone,
  whatsappDigits,
} from "../../../components/catalogue/catalogueShareCountries";
import { sendCatalogueGateOtp } from "../../../components/catalogue/catalogueSecurityApi";
import { openCatalogueSession } from "../../../components/catalogue/catalogueResellerApi";
import {
  OPEN_LINK_RETRY_HINT,
  viewerGateRequiresPhone,
} from "../../../components/catalogue/catalogueViewerGate";
import type { CatalogueViewerGate } from "../../../types/catalogue.types";

export interface PublicCatalogueGateFormProps {
  catalogueId: string;
  linkToken: string;
  gate: CatalogueViewerGate;
  catalogueTitle?: string;
  /** Who shared it, as in "Only {hostLabel} sees this". */
  hostLabel?: string;
  onUnlocked: (sessionToken: string, guest?: { displayName?: string; phone?: string }) => void;
}

const dialFor = (iso: string): string | undefined =>
  CATALOGUE_SHARE_COUNTRIES.find((c) => c.iso === iso)?.dial;

/**
 * A group link that asks who is viewing: name, mobile (optional or required) and, on OTP links,
 * an SMS code for Indian mobiles. One form, once; the viewer session then remembers them.
 */
const PublicCatalogueGateForm: React.FC<PublicCatalogueGateFormProps> = ({
  catalogueId,
  linkToken,
  gate,
  catalogueTitle,
  hostLabel = "the studio",
  onUnlocked,
}) => {
  const [name, setName] = useState("");
  const [countryIso, setCountryIso] = useState(DEFAULT_SHARE_COUNTRY_ISO);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [openFailed, setOpenFailed] = useState(false);

  const phoneRequired = viewerGateRequiresPhone(gate);
  const otpGate = gate === "OTP";
  const phoneTyped = phone.trim().length > 0;
  // SMS codes go to Indian mobiles only; anyone else continues without one.
  const codeApplies = otpGate && phoneTyped && countryIso === "IN";

  const warn = (title: string, message: string) =>
    notifications({ title, message, type: "warning" });

  const validate = (): boolean => {
    if (!name.trim()) {
      warn("Your name", "Please tell us your name.");
      return false;
    }
    if (phoneRequired && !phoneTyped) {
      warn("Mobile", "Please enter your mobile number.");
      return false;
    }
    if (phoneTyped && !isPlausibleSharePhone(phone, countryIso)) {
      warn("Mobile", countryIso === "IN" ? "Enter a 10-digit Indian mobile." : "Enter your local mobile number.");
      return false;
    }
    return true;
  };

  const sendCode = async (): Promise<"sent" | "skip" | "failed"> => {
    const res = await sendCatalogueGateOtp(catalogueId, {
      linkToken,
      phone: phone.trim(),
      countryCode: dialFor(countryIso),
    }).catch(() => null);
    if (res?.success) {
      setOtpSent(true);
      setMaskedPhone(res.data?.maskedPhone || "");
      return "sent";
    }
    if (res?.data?.otpAvailable === false) return "skip";
    notifications({
      title: "Code",
      message: res?.message || "Could not send the code. Try again.",
      type: "danger",
    });
    return "failed";
  };

  const open = async (withOtp?: string) => {
    const res = await openCatalogueSession(catalogueId, linkToken, {
      viewerName: name.trim(),
      viewerPhone: phoneTyped ? phone.trim() : undefined,
      viewerCountryCode: phoneTyped ? dialFor(countryIso) : undefined,
      otp: withOtp,
    }).catch(() => null);
    if (res?.success && res.data?.sessionToken) {
      onUnlocked(res.data.sessionToken, {
        displayName: res.data.recipientName || name.trim(),
        phone: phoneTyped ? whatsappDigits(countryIso, phone.trim()) : undefined,
      });
      return;
    }
    if (res?.data?.otpRequired) {
      setOtpSent(true);
      warn("Code", "Enter the code we sent to your mobile.");
      return;
    }
    setOpenFailed(true);
    notifications({
      title: "Could not open",
      message: res?.message || "Please try again.",
      type: "danger",
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !validate()) return;
    setBusy(true);
    try {
      if (codeApplies && !otpSent) {
        const sent = await sendCode();
        if (sent === "skip") await open();
        return;
      }
      if (codeApplies && otpSent) {
        if (!otp.trim()) {
          warn("Code", "Enter the code we sent to your mobile.");
          return;
        }
        await open(otp.trim());
        return;
      }
      await open();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      setOtp("");
      await sendCode();
    } finally {
      setBusy(false);
    }
  };

  const buttonLabel = busy
    ? "Please wait…"
    : codeApplies && !otpSent
    ? "Send code"
    : codeApplies && otpSent
    ? "Verify and continue"
    : "Continue";

  return (
    <div className="hkp-shared-gate" data-hkp-viewer-gate={gate}>
      <h2>Before you browse</h2>
      <p className="hkp-shared-gate-lede">
        {catalogueTitle ? `“${catalogueTitle}” is shared with people ${hostLabel} knows.` : `This collection is shared with people ${hostLabel} knows.`}{" "}
        Tell us who you are, once.
      </p>

      <form onSubmit={(e) => void submit(e)}>
        <label htmlFor="hkp-gate-name">Your name</label>
        <input
          id="hkp-gate-name"
          type="text"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          disabled={otpSent}
          maxLength={80}
        />

        <label htmlFor="hkp-gate-mobile">
          Mobile{phoneRequired ? "" : " (optional)"}
        </label>
        <div className="hkp-shared-gate-phone-row">
          <select
            id="hkp-gate-mobile-country"
            className="hkp-shared-gate-country"
            value={countryIso}
            onChange={(e) => setCountryIso(e.target.value)}
            disabled={otpSent}
            aria-label="Country code"
          >
            {CATALOGUE_SHARE_COUNTRIES.map((country) => (
              <option key={country.iso} value={country.iso}>
                {country.iso} +{country.dial}
              </option>
            ))}
          </select>
          <input
            id="hkp-gate-mobile"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={countryIso === "IN" ? "10-digit mobile" : "Local mobile"}
            disabled={otpSent}
            aria-label="Mobile number"
          />
        </div>
        {otpGate && phoneTyped && countryIso !== "IN" ? (
          <p className="hkp-shared-gate-hint" style={{ marginTop: 0 }}>
            SMS codes go to Indian mobiles only. You can continue without one.
          </p>
        ) : null}

        {codeApplies && otpSent ? (
          <>
            <label htmlFor="hkp-gate-code">One-time code</label>
            <input
              id="hkp-gate-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder={`Code sent to ${maskedPhone || "your mobile"}`}
            />
          </>
        ) : null}

        <button type="submit" className="hkp-shared-gate-submit" disabled={busy}>
          {buttonLabel}
        </button>
        {codeApplies && otpSent ? (
          <button type="button" className="hkp-shared-linkish hkp-shared-gate-alt" onClick={() => void resend()} disabled={busy}>
            Resend code
          </button>
        ) : null}
      </form>

      {openFailed && (
        <p className="hkp-shared-gate-hint" role="alert">
          {OPEN_LINK_RETRY_HINT}
        </p>
      )}
      <p className="hkp-shared-gate-hint">
        Only {hostLabel} sees this, to follow up on pieces you like. Nothing is shared further.
      </p>
    </div>
  );
};

export default PublicCatalogueGateForm;
